<script setup lang="ts">
import {TextInput, useClose} from "@hantera/design-system";
import {ref} from "vue";
import {useLocalization} from '@hantera/portal-app'
import useBackend from "@/composition/useBackend";

interface Props {
  onPasswordChanged?: () => void
}

const props = defineProps<Props>()

const { user } = useBackend()
const t = useLocalization('user')
const close = useClose()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

async function submit() {
  error.value = null

  // Validate current password
  if (!currentPassword.value?.trim()) {
    error.value = t('validation.currentPasswordRequired')
    return
  }

  // Validate new password
  if (!newPassword.value?.trim()) {
    error.value = t('validation.newPasswordRequired')
    return
  }

  // Validate confirm password
  if (!confirmPassword.value?.trim()) {
    error.value = t('validation.confirmPasswordRequired')
    return
  }

  // Check passwords match
  if (newPassword.value !== confirmPassword.value) {
    error.value = t('validation.passwordsMustMatch')
    return
  }

  // Check minimum length
  if (newPassword.value.length < 8) {
    error.value = t('validation.passwordTooShort')
    return
  }

  // Check complexity (uppercase, lowercase, digit)
  const hasUppercase = /[A-Z]/.test(newPassword.value)
  const hasLowercase = /[a-z]/.test(newPassword.value)
  const hasDigit = /\d/.test(newPassword.value)

  if (!hasUppercase || !hasLowercase || !hasDigit) {
    error.value = t('validation.passwordComplexity')
    return
  }

  loading.value = true

  try {
    await user.changePassword(currentPassword.value, newPassword.value)
    props.onPasswordChanged?.()
    close()
  } catch (err: any) {
    // Try to parse Hantera error model
    try {
      const errorResponse = JSON.parse(err.message)
      if (errorResponse.errors && errorResponse.errors.length > 0) {
        const errorCode = errorResponse.errors[0].code
        // Try to get localized error message for this code
        const localizedError = t(`errors.${errorCode}`)
        error.value = localizedError !== `errors.${errorCode}` ? localizedError : errorResponse.errors[0].message
      } else {
        error.value = err.message || t('errors.unexpectedError')
      }
    } catch {
      // Not JSON or different format, use as-is
      error.value = err.message || t('errors.unexpectedError')
    }
  } finally {
    loading.value = false
  }
}

function cancel() {
  close()
}
</script>

<template>
  <form @submit.prevent="submit" class="change-password-dialog">
    <div class="field">
      <label for="current-password">{{ t('password.current') }}</label>
      <TextInput 
        id="current-password" 
        type="password" 
        v-model="currentPassword" 
        :disabled="loading"
        autocomplete="password"
      />
    </div>

    <div class="field">
      <label for="new-password">{{ t('password.new') }}</label>
      <TextInput 
        id="new-password" 
        type="password" 
        v-model="newPassword" 
        :disabled="loading"
      />
    </div>

    <div class="field">
      <label for="confirm-password">{{ t('password.confirm') }}</label>
      <TextInput 
        id="confirm-password" 
        type="password" 
        v-model="confirmPassword" 
        :disabled="loading"
      />
    </div>

    <div v-if="error" class="box-danger p">{{ error }}</div>

    <div class="actions">
      <button type="button" @click="cancel" class="btn-silent" :disabled="loading">
        {{ t('actions.cancel') }}
      </button>
      <button type="submit" class="btn-primary" :disabled="loading">
        {{ loading ? t('actions.saving') : t('actions.saveChanges') }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.change-password-dialog {
  padding: 1rem;
  min-width: 400px;
}

.field {
  margin-bottom: 1rem;
}

.actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

@media (max-width: 768px) {
  .change-password-dialog {
    min-width: auto;
  }

  .actions {
    flex-direction: column;
  }

  .actions button {
    width: 100%;
  }
}
</style>
